import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Pagination,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  UserCheck,
  UserX,
  ArrowRightLeft,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { studentsApi, classroomsApi, Student, Classroom } from '../../services/schoolApi';
import ConfirmDialog from '../../components/ConfirmDialog';

const Students = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  
  const [students, setStudents] = useState<Student[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<string | null>(null);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [studentToTransfer, setStudentToTransfer] = useState<Student | null>(null);
  const [transferClassroomId, setTransferClassroomId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterGender, setFilterGender] = useState('');
  const [filterClassroom, setFilterClassroom] = useState('');

  const [formData, setFormData] = useState({
    matricule: '',
    firstName: '',
    lastName: '',
    middleName: '',
    gender: 'M' as 'M' | 'F',
    birthDate: '',
    classroomId: '',
    schoolYear: '2025-2026',
    tuteurName: '',
    tuteurPhone: '',
    status: 'actif' as 'actif' | 'transfert' | 'sorti',
  });

  const text = {
    fr: {
      title: 'Gestion des Élèves',
      description: 'Liste et gestion de tous les élèves',
      addStudent: 'Ajouter un élève',
      search: 'Rechercher un élève...',
      filterGender: 'Filtrer par genre',
      filterClass: 'Filtrer par classe',
      all: 'Tous',
      male: 'Garçon',
      female: 'Fille',
      firstName: 'Prénom',
      lastName: 'Nom',
      birthDate: 'Date de naissance',
      middleName: 'Nom du milieu',
      schoolYear: 'Année scolaire',
      tuteurName: 'Nom du tuteur',
      tuteurPhone: 'Tél. Tuteur',
      gender: 'Genre',
      classroom: 'Classe',
      matricule: 'Matricule',
      actions: 'Actions',
      edit: 'Modifier',
      delete: 'Supprimer',
      active: 'Actif',
      inactive: 'Inactif',
      cancel: 'Annuler',
      save: 'Enregistrer',
      status: 'Statut',
      editStudent: 'Modifier l\'élève',
      deleteConfirm: 'Êtes-vous sûr de vouloir supprimer cet élève ?',
      noStudents: 'Aucun élève trouvé',
      loading: 'Chargement...',
      transfer: 'Transférer',
      transferStudent: 'Transférer un élève',
      transferTo: 'Nouvelle classe',
      transferConfirm: 'Transférer',
    },
    en: {
      title: 'Students Management',
      description: 'List and manage all students',
      addStudent: 'Add Student',
      search: 'Search student...',
      filterGender: 'Filter by gender',
      filterClass: 'Filter by class',
      all: 'All',
      male: 'Male',
      female: 'Female',
      firstName: 'First Name',
      lastName: 'Last Name',
      birthDate: 'Date of Birth',
      middleName: 'Middle Name',
      schoolYear: 'School Year',
      tuteurName: 'Guardian Name',
      tuteurPhone: 'Guardian Phone',
      gender: 'Gender',
      classroom: 'Classroom',
      matricule: 'ID Number',
      actions: 'Actions',
      edit: 'Edit',
      delete: 'Delete',
      active: 'Active',
      inactive: 'Inactive',
      cancel: 'Cancel',
      save: 'Save',
      status: 'Status',
      editStudent: 'Edit Student',
      deleteConfirm: 'Are you sure you want to delete this student?',
      noStudents: 'No students found',
      loading: 'Loading...',
      transfer: 'Transfer',
      transferStudent: 'Transfer Student',
      transferTo: 'New classroom',
      transferConfirm: 'Transfer',
    }
  };

  const currentText = text[language as keyof typeof text];

  useEffect(() => {
    fetchStudents();
    fetchClassrooms();
  }, [page, searchTerm, filterGender, filterClassroom]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await studentsApi.getAll({
        page,
        limit: 10,
        search: searchTerm || undefined,
        gender: filterGender as 'M' | 'F' | undefined,
        classroomId: filterClassroom || undefined,
      });
      setStudents(response.data);
      setTotalPages(response.totalPages);
      setError('');
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des élèves');
    } finally {
      setLoading(false);
    }
  };

  const fetchClassrooms = async () => {
    try {
      const response = await classroomsApi.getAll({ limit: 100 });
      setClassrooms(response.data);
    } catch (err) {
      console.error('Erreur lors du chargement des classes:', err);
    }
  };

  const handleOpenDialog = (student?: Student) => {
    if (student) {
      setEditingStudent(student);
      setFormData({
        matricule: student.matricule,
        firstName: student.firstName,
        lastName: student.lastName,
        middleName: student.middleName || '',
        gender: student.gender,
        birthDate: student.birthDate ? student.birthDate.split('T')[0] : '',
        classroomId: typeof student.classroomId === 'string' ? student.classroomId : student.classroomId._id,
        schoolYear: student.schoolYear,
        tuteurName: student.tuteur?.name || '',
        tuteurPhone: student.tuteur?.phone || '',
        status: student.status,
      });
    } else {
      setEditingStudent(null);
      setFormData({
        matricule: '',
        firstName: '',
        lastName: '',
        middleName: '',
        gender: 'M',
        birthDate: '',
        classroomId: '',
        schoolYear: '2025-2026',
        tuteurName: '',
        tuteurPhone: '',
        status: 'actif',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStudent(null);
  };

  const handleSubmit = async () => {
    try {
      const data = {
        matricule: formData.matricule,
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName || undefined,
        gender: formData.gender,
        birthDate: formData.birthDate || undefined,
        classroomId: formData.classroomId,
        schoolYear: formData.schoolYear,
        tuteur: {
          name: formData.tuteurName || undefined,
          phone: formData.tuteurPhone || undefined,
        },
        status: formData.status,
      };
      
      if (editingStudent) {
        await studentsApi.update(editingStudent._id, data);
      } else {
        await studentsApi.create(data);
      }
      handleCloseDialog();
      fetchStudents();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteClick = (id: string) => {
    setStudentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (studentToDelete) {
      try {
        await studentsApi.delete(studentToDelete);
        setDeleteDialogOpen(false);
        setStudentToDelete(null);
        fetchStudents();
      } catch (err: any) {
        setError(err.message || 'Erreur lors de la suppression');
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setStudentToDelete(null);
  };

  const handleTransferClick = (student: Student) => {
    setStudentToTransfer(student);
    setTransferClassroomId('');
    setTransferDialogOpen(true);
  };

  const handleTransferConfirm = async () => {
    if (studentToTransfer && transferClassroomId) {
      try {
        await studentsApi.transfer(studentToTransfer._id, transferClassroomId);
        setTransferDialogOpen(false);
        setStudentToTransfer(null);
        setTransferClassroomId('');
        fetchStudents();
      } catch (err: any) {
        setError(err.message || 'Erreur lors du transfert');
      }
    }
  };

  const getClassroomName = (classroomId: string | Classroom) => {
    if (typeof classroomId === 'object') {
      return classroomId.name;
    }
    const classroom = classrooms.find(c => c._id === classroomId);
    return classroom?.name || '-';
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#0D3C4C', mb: 1 }}>
            {currentText.title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentText.description}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Plus size={20} />}
          onClick={() => handleOpenDialog()}
          sx={{ 
            backgroundColor: '#0D3C4C',
            '&:hover': { backgroundColor: '#1A5F7A' }
          }}
        >
          {currentText.addStudent}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={currentText.search}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>{currentText.filterGender}</InputLabel>
            <Select
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
              label={currentText.filterGender}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              <MenuItem value="M">{currentText.male}</MenuItem>
              <MenuItem value="F">{currentText.female}</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>{currentText.filterClass}</InputLabel>
            <Select
              value={filterClassroom}
              onChange={(e) => setFilterClassroom(e.target.value)}
              label={currentText.filterClass}
            >
              <MenuItem value="">{currentText.all}</MenuItem>
              {classrooms.map((classroom) => (
                <MenuItem key={classroom._id} value={classroom._id}>
                  {classroom.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.matricule}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.firstName}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.lastName}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.gender}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.classroom}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.tuteurPhone}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{currentText.actions}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }}>{currentText.loading}</Typography>
                </TableCell>
              </TableRow>
            ) : students.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">{currentText.noStudents}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              students.map((student) => (
                <TableRow key={student._id} hover>
                  <TableCell>{student.matricule || '-'}</TableCell>
                  <TableCell>{student.firstName}</TableCell>
                  <TableCell>{student.lastName}</TableCell>
                  <TableCell>
                    {student.gender === 'M' ? currentText.male : currentText.female}
                  </TableCell>
                  <TableCell>{getClassroomName(student.classroomId)}</TableCell>
                  <TableCell>{student.tuteur?.phone || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      icon={student.status === 'actif' ? <UserCheck size={16} /> : <UserX size={16} />}
                      label={student.status === 'actif' ? currentText.active : student.status}
                      color={student.status === 'actif' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(student)}
                      sx={{ color: '#0D3C4C' }}
                    >
                      <Edit size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleTransferClick(student)}
                      sx={{ color: '#1A5F7A' }}
                      title={currentText.transfer}
                    >
                      <ArrowRightLeft size={18} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(student._id)}
                      sx={{ color: '#d32f2f' }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingStudent ? currentText.editStudent : currentText.addStudent}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 2 }}>
            <TextField
              label={currentText.firstName}
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.lastName}
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.middleName}
              value={formData.middleName}
              onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
              fullWidth
            />
            <TextField
              label={currentText.birthDate}
              type="date"
              value={formData.birthDate}
              onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControl fullWidth required>
              <InputLabel>{currentText.gender}</InputLabel>
              <Select
                value={formData.gender}
                onChange={(e) => setFormData({ ...formData, gender: e.target.value as 'M' | 'F' })}
                label={currentText.gender}
              >
                <MenuItem value="M">{currentText.male}</MenuItem>
                <MenuItem value="F">{currentText.female}</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth required sx={{ gridColumn: '1 / -1' }}>
              <InputLabel>{currentText.classroom}</InputLabel>
              <Select
                value={formData.classroomId}
                onChange={(e) => setFormData({ ...formData, classroomId: e.target.value })}
                label={currentText.classroom}
              >
                {classrooms.map((classroom) => (
                  <MenuItem key={classroom._id} value={classroom._id}>
                    {classroom.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={currentText.matricule}
              value={formData.matricule}
              onChange={(e) => setFormData({ ...formData, matricule: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.schoolYear}
              value={formData.schoolYear}
              onChange={(e) => setFormData({ ...formData, schoolYear: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label={currentText.tuteurName}
              value={formData.tuteurName}
              onChange={(e) => setFormData({ ...formData, tuteurName: e.target.value })}
              fullWidth
            />
            <TextField
              label={currentText.tuteurPhone}
              value={formData.tuteurPhone}
              onChange={(e) => setFormData({ ...formData, tuteurPhone: e.target.value })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{currentText.cancel}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            sx={{ 
              backgroundColor: '#0D3C4C',
              '&:hover': { backgroundColor: '#1A5F7A' }
            }}
          >
            {currentText.save}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title={language === 'fr' ? 'Supprimer l\'élève' : 'Delete Student'}
        message={language === 'fr' 
          ? 'Êtes-vous sûr de vouloir supprimer cet élève ? Cette action est irréversible et supprimera toutes les données associées.'
          : 'Are you sure you want to delete this student? This action is irreversible and will delete all associated data.'}
        confirmText={language === 'fr' ? 'Supprimer' : 'Delete'}
        cancelText={currentText.cancel}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="danger"
      />

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentText.transferStudent}</DialogTitle>
        <DialogContent>
          {studentToTransfer && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {studentToTransfer.firstName} {studentToTransfer.lastName} ({studentToTransfer.matricule})
              </Typography>
              <FormControl fullWidth required>
                <InputLabel>{currentText.transferTo}</InputLabel>
                <Select
                  value={transferClassroomId}
                  onChange={(e) => setTransferClassroomId(e.target.value)}
                  label={currentText.transferTo}
                >
                  {classrooms
                    .filter(c => {
                      const currentClassId = typeof studentToTransfer.classroomId === 'string' 
                        ? studentToTransfer.classroomId 
                        : studentToTransfer.classroomId._id;
                      return c._id !== currentClassId;
                    })
                    .map((classroom) => (
                      <MenuItem key={classroom._id} value={classroom._id}>
                        {classroom.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)}>{currentText.cancel}</Button>
          <Button 
            onClick={handleTransferConfirm} 
            variant="contained"
            disabled={!transferClassroomId}
            sx={{ 
              backgroundColor: '#1A5F7A',
              '&:hover': { backgroundColor: '#0D3C4C' }
            }}
          >
            {currentText.transferConfirm}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Students;
